<script setup lang="ts">
type TabDef = { id: string; label: string };

const { t } = useI18n();

const {
  active,
  lockedTabs = [],
  hiddenTabs = [],
} = defineProps<{
  active: string;
  lockedTabs?: string[];
  hiddenTabs?: string[];
}>();

const emit = defineEmits<{
  (e: 'tab-click', key: string): void;
  (e: 'locked-click', key: string): void;
}>();

const allTabs = computed<TabDef[]>(() => [
  { id: 'general', label: t('property.access.accessTabsRow.generalInfo') },
  { id: 'location', label: t('property.shell.tabs.location') },
  { id: 'layouts', label: t('property.shell.tabs.layouts') },
  { id: 'availability', label: t('property.shell.tabs.availability') },
  { id: 'price', label: t('property.access.accessTabsRow.pricePlan') },
  { id: 'amenities', label: t('property.shell.tabs.amenities') },
  { id: 'developer', label: t('property.shell.tabs.developer') },
  { id: 'documents', label: t('property.shell.tabs.documents') },
]);

const visibleTabs = computed(() => allTabs.value.filter((tab) => !hiddenTabs.includes(tab.id)));

const isLocked = (id: string) => lockedTabs.includes(id);

const handleClick = (id: string) => {
  if (isLocked(id)) {
    emit('locked-click', id);
    return;
  }
  emit('tab-click', id);
};
</script>

<template>
  <div
    class="mb-[18px] flex items-center gap-1 overflow-x-auto border-b border-black/[0.08] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    role="tablist"
  >
    <button
      v-for="tab in visibleTabs"
      :key="tab.id"
      type="button"
      role="tab"
      class="relative inline-flex shrink-0 items-center whitespace-nowrap px-[18px] py-[14px] text-[14px] transition-colors"
      :class="[
        active === tab.id
          ? 'font-bold text-black-100'
          : isLocked(tab.id)
            ? 'font-medium text-black-100/40 hover:text-black-100/55'
            : 'font-medium text-black-60 hover:text-black-100',
      ]"
      :aria-selected="active === tab.id"
      @click="handleClick(tab.id)"
    >
      {{ tab.label }}
      <span
        v-if="isLocked(tab.id)"
        class="ms-1.5 inline-flex align-middle text-black-100/45"
        aria-hidden="true"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="4"
            y="11"
            width="16"
            height="10"
            rx="2"
          />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
      </span>
      <span
        v-if="active === tab.id"
        class="absolute inset-x-3 -bottom-px h-[2px] bg-black-100"
        aria-hidden="true"
      />
    </button>
  </div>
</template>
